import { ArrowsRightLeftIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { Button } from '@klokgroep/shared-components/src/Button';
import { FilteredItemsCount } from '@klokgroep/shared-components/utils/UseFilteredJobs';
import { RichText } from '@klokgroep/shared-components/src/RichText';
import cx from 'classnames';
import React, { ChangeEventHandler, ReactNode, useCallback, useMemo, useState } from 'react';
import styles from './Filters.module.css';
import { useCommonTranslations } from '@klokgroep/shared-components/utils/useTranslations';

interface Properties {
  children: ReactNode;
  title: string;
}

export const Filters = ({ children, title }: Properties) => {
  const [showFilters, setShowFilters] = useState(false);
  const t = useCommonTranslations();

  const toggleShowFilters = useCallback(() => {
    setShowFilters((showFilterState) => !showFilterState);
  }, []);

  const closeFilters = useCallback(() => setShowFilters(false), []);

  return (
    <div className={styles.container}>
      <div className={styles.buttonContainer}>
        <Button onClick={toggleShowFilters} theme="outline">
          <span className={styles.buttonInner}>
            <span>{title}</span>
            <ArrowsRightLeftIcon />
          </span>
        </Button>
      </div>
      <div className={cx(styles.filterContainer, { [styles.isActive]: showFilters })}>
        {children}
        <div className={styles.closeButton}>
          <Button onClick={closeFilters}>{t('apply_filters')}</Button>
        </div>
      </div>
    </div>
  );
};

interface FilterSectionProperties {
  collapsible?: boolean;
  filterCount?: FilteredItemsCount[];
  horizontal?: boolean;
  items?: { label?: string; id?: string; checked: boolean }[];
  onChange: (value: string, checked: boolean) => void;
  title?: string;
  titleAs?: 'h3' | 'h4';
  markInactive?: boolean;
}

export const FilterSection = ({
  collapsible,
  filterCount,
  horizontal = false,
  items,
  onChange,
  title,
  titleAs = 'h3',
  markInactive,
}: FilterSectionProperties) => {
  const [collapsed, setCollapsed] = useState(false);
  const t = useCommonTranslations();

  const HeaderElement = collapsible ? 'button' : 'header';
  const TitleAs = titleAs;

  const onChangeFilters: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      onChange(event.target.value, event.target.checked);
    },
    [onChange]
  );

  const itemsSortedByLabel = useMemo(
    () => items?.sort((a, b) => ((a.label || '') > (b.label || '') ? 1 : -1)),
    [items]
  );

  const atLeastOneChecked = useMemo(() => items?.some((item) => item.checked), [items]);

  return (
    <section className={horizontal ? styles.horizontal : undefined}>
      <HeaderElement
        className={cx(styles.filterSectionHeader, { [styles.collapsed]: collapsible && collapsed })}
        onClick={collapsible ? () => setCollapsed((current) => !current) : undefined}
        type={collapsible ? 'button' : undefined}>
        <RichText>
          <TitleAs>
            <strong>{title}</strong>
          </TitleAs>
        </RichText>
        {collapsible ? <ChevronDownIcon /> : undefined}
      </HeaderElement>

      <ul
        className={cx(styles.filterList, {
          [styles.collapsed]: collapsible && collapsed,
          [styles.horizontal]: horizontal,
        })}>
        {itemsSortedByLabel?.map((item) =>
          (filterCount?.find((countItem) => (countItem.id as unknown as string) === item.id)?.count || 0) > 0 ||
          !filterCount ? (
            <li key={item.label} className={styles.filterListItem}>
              <label className={!markInactive || item.checked || !atLeastOneChecked ? undefined : styles.unCheckedItem}>
                <input type="checkbox" value={item.id} checked={item.checked} onChange={onChangeFilters} />
                {item.label}{' '}
                {filterCount
                  ? `(${filterCount?.find((countItem) => (countItem.id as unknown as string) === item.id)?.count})`
                  : undefined}
              </label>
            </li>
          ) : undefined
        )}
        {(itemsSortedByLabel || []).length === 0 ? (
          <li className={styles.filterListItem}>{t('no_filters')}</li>
        ) : undefined}
      </ul>
    </section>
  );
};
